import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as riskModelsApi from "@/lib/api/risk-models";
import { useInvalidateFlowCachesForCollectionEdit } from "./use-flow-cache-invalidation";

// --- Query hooks ---

export function useRiskModels() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["riskModels"],
    queryFn: riskModelsApi.list,
  });
  return { models: data ?? [], error, isLoading };
}

export function useActiveRiskModels() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["riskModels", "active"],
    queryFn: riskModelsApi.listActive,
  });
  return { models: data ?? [], error, isLoading };
}

export function useRiskModel(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["riskModels", id],
    queryFn: () => riskModelsApi.getById(id!),
    enabled: !!id,
    // staleTime: Infinity, // Risk model config is reference data; mutations invalidate when it changes
  });
  return { model: data ?? null, error, isLoading };
}

// --- Model mutation hooks ---

export function useCreateRiskModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: riskModelsApi.create,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["riskModels"] }),
  });
}

export function useCreateRiskModelFromTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: riskModelsApi.createFromTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["riskModels"] }),
  });
}

// QL-212 — the clone is a brand-new model no flow or goal references yet, so
// the list key is all that moves (same as creating from a template).
export function useCloneRiskModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof riskModelsApi.clone>[1] }) =>
      riskModelsApi.clone(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["riskModels"] }),
  });
}

export function useUpdateRiskModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof riskModelsApi.update>[1] }) =>
      riskModelsApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["riskModels"] }),
  });
}

export function useToggleRiskModelActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: riskModelsApi.toggleActive,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["riskModels"] }),
  });
}

export function useDeleteRiskModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: riskModelsApi.remove,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["riskModels"] }),
  });
}

// --- Time Horizon mutation hooks ---

export function useCreateTimeHorizon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      modelId,
      data,
    }: {
      modelId: string;
      data: Parameters<typeof riskModelsApi.createTimeHorizon>[1];
    }) => riskModelsApi.createTimeHorizon(modelId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["riskModels"] }),
  });
}

export function useUpdateTimeHorizon() {
  const queryClient = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      thId,
      data,
    }: {
      thId: string;
      data: Parameters<typeof riskModelsApi.updateTimeHorizon>[1];
    }) => riskModelsApi.updateTimeHorizon(thId, data),
    // Goals on this horizon get their snapshots wiped server-side.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["riskModels"] });
      invalidateFlowCaches();
    },
  });
}

export function useDeleteTimeHorizon() {
  const queryClient = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: riskModelsApi.deleteTimeHorizon,
    // Goals on this horizon get their snapshots wiped server-side.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["riskModels"] });
      invalidateFlowCaches();
    },
  });
}

// --- Risk Level mutation hooks ---

export function useCreateRiskLevel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      modelId,
      data,
    }: {
      modelId: string;
      data: Parameters<typeof riskModelsApi.createRiskLevel>[1];
    }) => riskModelsApi.createRiskLevel(modelId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["riskModels"] }),
  });
}

export function useUpdateRiskLevel() {
  const queryClient = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      rlId,
      data,
    }: {
      rlId: string;
      data: Parameters<typeof riskModelsApi.updateRiskLevel>[1];
    }) => riskModelsApi.updateRiskLevel(rlId, data),
    // Goals on this level get their snapshots wiped server-side.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["riskModels"] });
      invalidateFlowCaches();
    },
  });
}

export function useDeleteRiskLevel() {
  const queryClient = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: riskModelsApi.deleteRiskLevel,
    // Goals on this level get their snapshots wiped server-side.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["riskModels"] });
      invalidateFlowCaches();
    },
  });
}

// --- Matrix Score mutation hook ---

export function useUpsertMatrixScore() {
  const queryClient = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      modelId,
      data,
    }: {
      modelId: string;
      data: Parameters<typeof riskModelsApi.upsertMatrixScore>[1];
    }) => riskModelsApi.upsertMatrixScore(modelId, data),
    // The matrix defines the risk scores products are matched against.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["riskModels"] });
      invalidateFlowCaches();
    },
  });
}
